import type { ElementType } from "react";
import AllInboxIcon from "@material-ui/icons/AllInbox";
import AppsIcon from "@material-ui/icons/Apps";
import BrushIcon from "@material-ui/icons/Brush";
import BuildIcon from "@material-ui/icons/Build";
import DashboardIcon from "@material-ui/icons/Dashboard";
import DescriptionIcon from "@material-ui/icons/Description";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PeopleIcon from "@material-ui/icons/People";
import StorefrontIcon from "@material-ui/icons/Storefront";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import type { SupplierRbacPermission } from "../../types";

/**
 * S1 (odd/tasks/rbac-by-field.md): the role dialog groups every module into
 * submenus and then lists the specific permissions inside each one. The
 * submenu is derived from the permission `action` only, so the backend contract
 * (`GET /v2/rbac/permissions`) stays untouched.
 */

/** One submenu of a module: its canonical/explicit label and its options. */
export interface PermissionSubmenu {
  /** Stable group identity: the canonical token (`ver`, `crear`, …) or the action. */
  key: string;
  /** Visible submenu label, in Spanish. */
  label: string;
  /** Options of this submenu, in API order. */
  permissions: SupplierRbacPermission[];
}

/** One module card of the dialog. */
export interface PermissionModuleGroup {
  module: string;
  submenus: PermissionSubmenu[];
}

/** S1: canonical submenus, rendered first and always in this order. */
export const CANONICAL_SUBMENU_LABELS = [
  "Ver",
  "Crear",
  "Editar",
  "Eliminar",
] as const;

/** S1: explicit label map for the non-canonical actions of the catalogue. */
export const SUBMENU_LABELS: Record<string, string> = {
  aprobar: "Aprobar",
  rechazar: "Rechazar",
  cambiar_estado: "Cambiar estado",
  agregar_guia: "Agregar guía",
  agregar_nota: "Agregar nota",
  compartir: "Compartir",
  duplicar: "Duplicar",
  reservar: "Reservar",
  trasladar: "Trasladar",
  finalizar: "Finalizar",
  marcar_recibido: "Marcar recibido",
  asignar_inventario: "Asignar inventario",
  cargar_entregables: "Cargar entregables",
  bandeja_entrada: "Bandeja de entrada",
  gestionar: "Gestionar",
  descargar_pdf: "Descargar PDF",
};

/** Same order as CANONICAL_SUBMENU_LABELS, used for the group identity. */
const CANONICAL_SUBMENU_KEYS = [
  "ver",
  "crear",
  "editar",
  "eliminar",
] as const;

type CanonicalSubmenuKey = (typeof CANONICAL_SUBMENU_KEYS)[number];

const capitalizeFirstLetter = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Canonical token of an action, matched on the action family token: `ver` and
 * `ver_*` belong to Ver, while `versionar`/`verificar` do not.
 */
const canonicalKeyForAction = (action: string): CanonicalSubmenuKey | null => {
  for (const key of CANONICAL_SUBMENU_KEYS) {
    if (action === key || action.startsWith(`${key}_`)) {
      return key;
    }
  }

  return null;
};

/** Group identity used to merge every member of a canonical family. */
export const submenuKeyForAction = (action: string): string => {
  const normalizedAction = action.trim().toLowerCase();

  return canonicalKeyForAction(normalizedAction) ?? normalizedAction;
};

/** Visible submenu label of an action (S1). */
export const submenuLabelForAction = (action: string): string => {
  const normalizedAction = action.trim().toLowerCase();
  const canonicalKey = canonicalKeyForAction(normalizedAction);

  if (canonicalKey) {
    return capitalizeFirstLetter(canonicalKey);
  }

  const explicitLabel = SUBMENU_LABELS[normalizedAction];
  if (explicitLabel) {
    return explicitLabel;
  }

  // Fallback for an unknown action: `_` becomes a space, first letter upper.
  return capitalizeFirstLetter(normalizedAction.replace(/_/g, " "));
};

const canonicalRank = (key: string) => {
  const index = (CANONICAL_SUBMENU_KEYS as readonly string[]).indexOf(key);

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

/**
 * Groups the permissions of ONE module into submenus: the canonical ones that
 * exist first (Ver, Crear, Editar, Eliminar), then the rest in the order their
 * first permission appears in the API response.
 */
export const groupPermissionsBySubmenu = (
  permissions: SupplierRbacPermission[]
): PermissionSubmenu[] => {
  const submenus = new Map<string, PermissionSubmenu>();

  permissions.forEach((permission) => {
    const key = submenuKeyForAction(permission.action);
    const existing = submenus.get(key);

    if (existing) {
      existing.permissions.push(permission);
      return;
    }

    submenus.set(key, {
      key,
      label: submenuLabelForAction(permission.action),
      permissions: [permission],
    });
  });

  // Array#sort is stable, but the appearance index keeps the order explicit:
  // canonical submenus first, everything else by first appearance.
  return Array.from(submenus.values())
    .map((submenu, appearanceIndex) => ({ submenu, appearanceIndex }))
    .sort(
      (left, right) =>
        canonicalRank(left.submenu.key) - canonicalRank(right.submenu.key) ||
        left.appearanceIndex - right.appearanceIndex
    )
    .map(({ submenu }) => submenu);
};

/**
 * Groups the whole catalogue into module cards, keeping the API module order
 * (`GET /v2/rbac/permissions` is ordered by `module` and `action`).
 */
export const groupPermissionsByModule = (
  permissions: SupplierRbacPermission[]
): PermissionModuleGroup[] => {
  const modules = new Map<string, SupplierRbacPermission[]>();

  permissions.forEach((permission) => {
    const modulePermissions = modules.get(permission.module);

    if (modulePermissions) {
      modulePermissions.push(permission);
      return;
    }

    modules.set(permission.module, [permission]);
  });

  return Array.from(modules.entries()).map(([module, modulePermissions]) => ({
    module,
    submenus: groupPermissionsBySubmenu(modulePermissions),
  }));
};

/**
 * S2: icon of an unknown module, so a new backend module never renders an
 * empty header.
 */
export const DEFAULT_MODULE_ICON: ElementType = AppsIcon;

/**
 * S2: explicit module -> icon map. Every icon comes from
 * `@material-ui/icons` v4 (no MUI v5 names such as `Inventory`).
 */
export const MODULE_ICONS: Record<string, ElementType> = {
  dashboard: DashboardIcon,
  cotizaciones: DescriptionIcon,
  inventario: StorefrontIcon,
  reservas: EventAvailableIcon,
  contenedores: AllInboxIcon,
  despachos: LocalShippingIcon,
  instalaciones: BuildIcon,
  solicitudes_diseño: BrushIcon,
  roles: VerifiedUserIcon,
  usuarios: PeopleIcon,
};

/** S2: pure helper the dialog uses to resolve the icon of a module card. */
export const moduleIconFor = (module: string): ElementType => {
  const normalizedModule = module.trim().toLowerCase();

  // `hasOwnProperty` keeps inherited keys (`constructor`, `toString`, …) on the
  // default icon, as any other unknown module.
  return Object.prototype.hasOwnProperty.call(MODULE_ICONS, normalizedModule)
    ? MODULE_ICONS[normalizedModule]
    : DEFAULT_MODULE_ICON;
};
